/**
 * UI adapter: bridges org dashboards to HireEmployee / OnboardVendor / ScheduleAudit (UC-30–32)
 */
import { architectureFeatures } from '../../../infrastructure/config/features';
import {
  hireEmployeeUseCase,
  onboardVendorUseCase,
  scheduleAuditUseCase,
} from '../../../infrastructure/di/container';
import { HireEmployeeUseCase } from '../../../application/use-cases/HireEmployeeUseCase';
import { OnboardVendorUseCase } from '../../../application/use-cases/OnboardVendorUseCase';
import { ScheduleAuditUseCase } from '../../../application/use-cases/ScheduleAuditUseCase';
import type {
  HireEmployeeInput,
  HireEmployeeOutput,
} from '../../../application/use-cases/HireEmployeeUseCase';
import type {
  OnboardVendorInput,
  OnboardVendorOutput,
} from '../../../application/use-cases/OnboardVendorUseCase';
import type {
  ScheduleAuditInput,
  ScheduleAuditOutput,
} from '../../../application/use-cases/ScheduleAuditUseCase';
import { orgManagementService } from '../../../../services/orgManagementService';

export type {
  HireEmployeeInput,
  HireEmployeeOutput,
  OnboardVendorInput,
  OnboardVendorOutput,
  ScheduleAuditInput,
  ScheduleAuditOutput,
};

export function resolveHireEmployee(input: HireEmployeeInput): HireEmployeeOutput {
  if (architectureFeatures.useOrgActionUseCases) {
    return hireEmployeeUseCase.execute(input);
  }
  try {
    const employee = orgManagementService.hireEmployee(input.type, input.currentOrgSize);
    return { ok: true, employee };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export function resolveOnboardVendor(input: OnboardVendorInput): OnboardVendorOutput {
  if (architectureFeatures.useOrgActionUseCases) {
    return onboardVendorUseCase.execute(input);
  }
  try {
    const vendor = orgManagementService.onboardVendor(input.vendorData);
    return { ok: true, vendor };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

export function resolveScheduleAudit(input: ScheduleAuditInput): ScheduleAuditOutput {
  if (architectureFeatures.useOrgActionUseCases) {
    return scheduleAuditUseCase.execute(input);
  }
  try {
    const audit = orgManagementService.scheduleAudit(input.type, input.targetDate);
    return { ok: true, audit };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

/** Legacy fallback constructors for journey-matrix tests */
export function executeLegacyHireEmployee(input: HireEmployeeInput): HireEmployeeOutput {
  return new HireEmployeeUseCase({
    orgManagement: {
      hireEmployee: (type, size) => orgManagementService.hireEmployee(type, size ?? 0),
    } as never,
  }).execute(input);
}